import type { TDateString } from '../types/common';
import {
  add,
  differenceInDays,
  getISODay,
  isAfter,
  isEqual,
  isLastDayOfMonth,
  isValid,
  lastDayOfMonth,
  max,
  min,
  parseISO,
  subDays,
} from 'date-fns';
import type { TDate, TTimeObject } from '../types/dateTime';
import { MS_PER_DAY } from '../constants/date';
import { getDateString } from './scheduleEntry';

export function toISODate<T extends Date | string>(date: T): string;
export function toISODate(date: null | undefined): null;
export function toISODate(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  // 'sv-SE': Sweden uses the ISO Date Format: yyyy-mm-dd
  return new Date(date).toLocaleDateString('sv-SE');
}

export function isoWeekdayOf(date: TDateString): number {
  return getISODay(parseISO(date));
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** True only for a `yyyy-MM-dd` string naming a date that exists on the calendar. */
export function isISODateString(value: unknown): value is TDateString {
  return typeof value === 'string' && ISO_DATE_PATTERN.test(value) && isValid(parseISO(value));
}

/** The year of an ISO date or date-time string, read from its first four characters. */
export function getIsoDateYear(date: TDateString): number {
  return Number(date.slice(0, 4));
}

/** True for a date-fns getISODay() result of 6 (Saturday) or 7 (Sunday). */
export function isWeekendIsoDay(isoDay: number): boolean {
  return isoDay === 6 || isoDay === 7;
}

/* Plain ISO-string comparison, not date-parsed - matches how valid_from/valid_until are
   stored and compared everywhere this guards a range (DB rows, request payloads, form
   fields), where a `<` comparison of two 'YYYY-MM-DD' strings already sorts correctly.
   An empty string counts as no bound, like null/undefined - a cleared type="date" input
   yields '', and '' < any date string is true in JS, which would otherwise misflag it. */
export function isInvalidDateRange(validFrom: unknown, validUntil: unknown): boolean {
  return typeof validFrom === 'string' && validFrom !== ''
    && typeof validUntil === 'string' && validUntil !== ''
    && validUntil < validFrom;
}

const parseDateCache = new Map<string, Date >();
export function parseDate(date?: TDate) {
  if (!date) {
    return null;
  }

  if (typeof date === 'string') {
    const cachedResult = parseDateCache.get(date);
    if (cachedResult) {
      return cachedResult;
    }

    const result = parseISO(date.length === 10 ? date + 'T00:00:00Z' : date);

    parseDateCache.set(date, result);
    return result;
  }

  return new Date(date);
}

/*
 * Month math in date-fns runs in local time; on UTC-midnight dates that shifts whole months by
 * one across a DST change. Reading the UTC calendar day as local midnight avoids it.
 */
export function toLocalCalendarDay(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// This function interprets `null` for a given date as 'far in the future' (= after the other date)
function isDateAfter(date: Date | null, start: Date | null, inclusive: boolean): boolean {
  if (!date && !start) {
    return inclusive;
  }
  return !date || !!start && (isAfter(date, start) || (inclusive && isEqual(date, start)));
}

export function isDateBetween(
  date: TDate,
  startDate: TDate,
  endDate: TDate,
  inclusive: boolean,
): boolean {
  const parsedDate = parseDate(date);
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  return isDateAfter(end, parsedDate, inclusive) && isDateAfter(parsedDate, start, inclusive);
}

export function hasDateOverlap<T extends TTimeObject = TTimeObject>(
  intervalObject: T,
  startDate: TDate,
  endDate: TDate,
  inclusive: 'start' | 'end' | boolean = true,
): boolean {
  const start = parseDate(startDate);
  const objectStart = parseDate(intervalObject.start);

  if (!start || !objectStart) {
    return false;
  }

  const end = parseDate(endDate);
  const objectEnd = parseDate(intervalObject.end);
  const inclusiveStart = ['start', true].includes(inclusive);
  const inclusiveEnd = ['end', true].includes(inclusive);

  const startsBeforeEnd = isDateAfter(end, objectStart, inclusiveEnd);
  const endsAfterStart = isDateAfter(objectEnd, start, inclusiveStart);

  return startsBeforeEnd && endsAfterStart;
}

/*
 * Canonical inclusive (whole-day) date helpers. NB for reviewers: several
 * frontend components (e.g. Configuration.vue, TimespanHistoriesTable.vue) and
 * the schedule solver still carry their own *fractional* day/overlap math.
 * Consolidating those onto these shared helpers is intentionally deferred to a
 * separate cleanup — it touches board rendering and the solver, so it needs the
 * app running to verify widths/behaviour rather than being done blind here.
 */

/** Inclusive number of days in a date range; 0 when invalid or the end precedes the start. */
export function getInclusiveDayCount(start: TDate, end: TDate): number {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) return 0;
  return Math.max(0, differenceInDays(to, from) + 1);
}

/** Inclusive number of overlapping days between two date ranges; 0 when they don't overlap. */
export function getOverlapDays(startA: TDate, endA: TDate, startB: TDate, endB: TDate): number {
  const aStart = parseDate(startA);
  const aEnd = parseDate(endA);
  const bStart = parseDate(startB);
  const bEnd = parseDate(endB);
  if (!aStart || !aEnd || !bStart || !bEnd) return 0;
  return Math.max(0, differenceInDays(min([aEnd, bEnd]), max([aStart, bStart])) + 1);
}

export function addMonthsDuration(startDate: Date | string, months: number): string {
  if (months <= 0) return toISODate(startDate);

  let endDate = add(startDate, { months: months });
  if (new Date(startDate).getDate() > endDate.getDate() || isLastDayOfMonth(startDate)) {
    // If target month doesn't have start day OR start is last day of month:
    endDate = lastDayOfMonth(endDate);
  } else {
    endDate = subDays(endDate, 1);
  }
  return toISODate(endDate);
}

/** A `YYYY-MM-DD` key `days` later, in UTC so the result does not depend on the local timezone. */
export function addDaysToDateKey(dateKey: TDateString, days: number): TDateString {
  const date = parseDate(dateKey);
  return date ? getDateString(new Date(date.getTime() + days * MS_PER_DAY)) : dateKey;
}

/* `addMonthsDuration` reads and formats in local time, so the key is parsed as local midnight. */
export function addMonthsToDateKey(dateKey: TDateString, months: number): TDateString {
  return addMonthsDuration(parseISO(dateKey), months);
}

/** `2027-03-08` → `08.03.2027`, the form every German-language notification and hint uses. */
export function formatGermanDate(date: TDateString): string {
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

/** `08.03.2027 bis 12.03.2027`; a single day - a missing or equal end - reads as one date. */
export function formatGermanDateRange(start: TDateString, end: TDateString | null): string {
  const last = end ?? start;
  if (last === start) return formatGermanDate(start);
  return `${formatGermanDate(start)} bis ${formatGermanDate(last)}`;
}
